import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import { Map, useMap } from "react-mapkit";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { Beacon } from "../../../../types/models/Beacon";
import {
  getBusinessBeaconsInGroup,
  getBusinessBeaconsInGroupByCategory,
  getBusinessBeaconsInGroupTextSearch,
  getNearbyBeacons,
  updateBeacon,
  showMessage,
} from "../../../../redux/actions";
import { getCategoryColor, convertToUpdateBeaconCoords } from "../../../../utils/utils";
import { AppConstants, DEFAULT_MAP_LOCATION } from "../../../../types/Constants";
import { BeaconListContexts } from "../list/BeaconListTypes";
import { Coordinate } from "../../../../types/models/Mapkit";
import { getCategories } from "../../../../redux/actions/Category";
import { useParams } from "react-router-dom";

const TOKEN_TTL_MS = 5 * 60 * 1000;
interface MapViewProps {
  onOpenBeaconDialog: (group_id: string, beacon?: Beacon, edit?: boolean) => void;
  refreshFlag: boolean;
  setRefreshFlag: (flag: boolean) => void;
  context: (typeof BeaconListContexts)[number];
}

const MapView: React.FC<MapViewProps> = ({
  onOpenBeaconDialog,
  refreshFlag,
  setRefreshFlag,
  context,
}) => {
  // Util
  const dispatch = useAppDispatch();
  const groupRef = useRef<string>();
  const { map, mapkit, mapProps } = useMap();
  const { group_id } = useParams<{ group_id: string }>();
  // Reducers
  const businessGroupState = useAppSelector((state) => state.businessGroup);
  const beaconState = useAppSelector((state) => state.beacon);
  const categoryGroupState = useAppSelector((state) => state.category);

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [moderationCoord, setModerationCoord] =
    useState<Coordinate>(DEFAULT_MAP_LOCATION);
  const [showZoomWarn, setShowZoomWarn] = useState<boolean>(false);

  useEffect(() => {
    // Try to get map center via browser, then get nearby beacons.
    initialiseMapCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    addMapEventListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (businessGroupState.currentBusinessGroup) {
      context === "BEACONS" && getBeaconData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessGroupState.currentBusinessGroup]);

  useEffect(() => {
    context === "MODERATION" && getBeaconData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, moderationCoord]);

  useEffect(() => {
    if (refreshFlag) {
      callGettingBeaconsForFirstPage();
      setRefreshFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag]);

  useEffect(() => {
    if (beaconState.searchText !== null) {
      callGettingBeaconsForFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beaconState.searchText]);

  useEffect(() => {
    if (beaconState.filterCategory !== null) {
      callGettingBeaconsForFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beaconState.filterCategory]);

  useEffect(() => {
    if (
      (businessGroupState.businessGroups?.length ?? 0) > 0 &&
      !isLoading
    ) {
      const currentGroup = businessGroupState.currentBusinessGroup?.id ?? "";
      if (group_id == currentGroup) {
        groupRef.current = group_id;
        createMarkers();
      }
      if (beaconState.totalBeaconCount > 0) {
        fitToMarkers();
      } else {
        setDefaultRegion();
      }
      

      return () => {
        removeMapEventListener();
      };
    }
  }, [isLoading, beaconState.currentBeacons, group_id]);

  const initialiseMapCenter = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setModerationCoord({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (positionError) => {
        console.log(
          `Using a default location because failed to get current position. Code:${positionError.code} Message:${positionError.message}`
        );
      }
    );
  };

  const getBeaconData = () => {
    switch (context) {
      case "BEACONS":
        getBusinessBeacons();
        break;
      case "MODERATION":
        getLatestOrNearbyBeacons();
        break;
      default:
        getBusinessBeacons();
        break;
    }
  };

  const getCategoryData = () => {
    return new Promise((resolve, reject) => {
        const group_id = businessGroupState.currentBusinessGroup!.id;
        dispatch(
          getCategories(group_id,(categories) => {
            if (categories) {
              resolve(categories);
            } else {
              reject(new Error("Failed to retrieve categories"));
            }
          })
        );
    });
  };
  

  const getLatestOrNearbyBeacons = () => {
    if (moderationCoord && map && map.region !== undefined) {
      setIsLoading(true);

      // Gets visible area of map, converts it to a coordinate region, then gets radius.
      const mapRadius = map.visibleMapRect.toCoordinateRegion().radius;

      // If radius is low enough, query for nearby beacons.
      if (mapRadius >= 1000) {
        setShowZoomWarn(true);
        setIsLoading(false);
      } else {
        setShowZoomWarn(false);
        dispatch(
          getNearbyBeacons(
            moderationCoord?.latitude,
            moderationCoord?.longitude,
            mapRadius,
            () => {
              setIsLoading(false);
            }
          )
        );
      }
    } else {
      setIsLoading(false);
    }
  };

  const callGettingBeaconsForFirstPage = () => {
    if (currentPage === 0 && !isLoading) {
      getBeaconData();
    } else {
      setCurrentPage(0);
    }
  };

  const handleLongPressOnMap = (event: any) => {
    try {
      const coordinate = map.convertPointOnPageToCoordinate(event.pointOnPage);
      const newBeacon: Beacon = {
        id: "",
        display_name: "",
        active: false,
        start_time: "",
        end_time: "",
        last_modified: "",
        latitude: coordinate.latitude,
        longitude: coordinate.longitude,
        altitude: null,
        category: null,
        summary: null,
        link: null,
        views: 0,
        likes: 0,
        description: null,
        image_id: undefined,
        image_ids: [],
        has_custom_beacon_face: false,
        model: "default",
        show_distance: true
      };
      onOpenBeaconDialog(groupRef.current ?? "", newBeacon, false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectAnnotation = (event: any) => {
    try {
      const selectedBeacon: Beacon = event.annotation.data;
      onOpenBeaconDialog(groupRef.current ?? "", selectedBeacon, true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDragAnnotation = (event: any) => {
    try {
      const selectedBeacon: Beacon = event.annotation.data;
      const newCoordinate = event.annotation.coordinate;

      // Update the local beacon object
      selectedBeacon.latitude = newCoordinate.latitude;
      selectedBeacon.longitude = newCoordinate.longitude;

      // Update beacon on the server
      const updateRequest = convertToUpdateBeaconCoords(selectedBeacon, businessGroupState.currentBusinessGroup?.id ?? "", newCoordinate.latitude, newCoordinate.longitude);

      dispatch(updateBeacon(updateRequest, (result) => {
        if (result) {
          dispatch(showMessage("The Beacon has been updated"));
        }
        else {
          // Do something here potentially.
        }
      }));

    } catch (error) {
      console.log(error);
    }
  };

  function addMapEventListener() {

    if (map) {
      // Don't add the long-press handler when moderating.
      context !== "MODERATION" &&
        map.addEventListener("long-press", handleLongPressOnMap);
      //  Only add drag-reloads when moderating.
      context === "MODERATION" &&
      map.addEventListener("region-change-end", handleRegionChange);
      map.addEventListener("select", handleSelectAnnotation);
      map.addEventListener("drag-end", handleDragAnnotation);

      return () => removeMapEventListener();
    }
  }

  const handleRegionChange = (event: any) => {
    // Grab the center of the map when a drag-event ends. This triggers an effect
    // That refetches data around that center.
    const coordinate = event.target.center;
    coordinate && setModerationCoord(coordinate);
  };

  function removeMapEventListener() {
    if (map) {
      map.removeEventListener("long-press", handleLongPressOnMap);
      map.removeEventListener("region-change-end", handleRegionChange);
      map.removeEventListener("select", handleSelectAnnotation);
      map.removeEventListener("drag-end", handleDragAnnotation);
    }
  }

  function getBusinessBeacons() {
    setIsLoading(true);

    getCategoryData()
      .then(() => {
        if (beaconState.searchText && beaconState.searchText.length > 0) {
          dispatch(
            getBusinessBeaconsInGroupTextSearch(
              businessGroupState.currentBusinessGroup?.id ?? null,
              beaconState.searchText,
              currentPage,
              () => {
                setIsLoading(false);
              }
            )
          );
        } else if (
          beaconState.filterCategory &&
          beaconState.filterCategory !== AppConstants.AllCategoriesValue
        ) {
          dispatch(
            getBusinessBeaconsInGroupByCategory(
              businessGroupState.currentBusinessGroup?.id ?? null,
              beaconState.filterCategory,
              currentPage,
              () => {
                setIsLoading(false);
              }
            )
          );
        } else {
          dispatch(
            getBusinessBeaconsInGroup(
              businessGroupState.currentBusinessGroup?.id ?? null, false,
              currentPage,
              () => {
                setIsLoading(false);
              }
            )
          );
        }
      }).catch((error) => {
        console.error(error);
      });
  }

  function setDefaultRegion() {
      if (map) {
        const coordinate = DEFAULT_MAP_LOCATION;
        const center = new mapkit.Coordinate(coordinate.latitude, coordinate.longitude);
        const span = new mapkit.CoordinateSpan(300, 300);
        const region = new mapkit.CoordinateRegion(center, span);
        map.setRegionAnimated(region);
      }
  }

  function fitToMarkers() {
    // Move map area and adjust zoom to display pins
      if (map && map.annotations) {
        map.showItems(map.annotations, {
          animate: true,
        });
      }
  }
  

  const createMarkers = () => {
    if ( map && map.annotations) {
      map.removeAnnotations(map.annotations);
      if (beaconState.currentBeacons && beaconState.currentBeacons.length > 0) {
        beaconState.currentBeacons?.forEach((beacon) => {
          const mapkitCoordinate = new mapkit.Coordinate(
            beacon.latitude,
            beacon.longitude
          );
          map.addAnnotation(
            new mapkit.MarkerAnnotation(mapkitCoordinate, {
              color: getCategoryColor(beacon.category ?? "", categoryGroupState.categories ?? []),
              title: beacon.display_name,
              data: beacon,
              draggable: true
            })
          );
        });
      }
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      { isLoading ? (
        <Box
          sx={{
            position: "absolute",
            height: "calc(100vh - 320px)",
            width: "calc(100vw - 276px)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" size={60} />
        </Box>
      ) : (<Box> </Box>)}
        <Box
          sx={{
            width: "100%",
            height: "calc(100vh - 320px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Map {...mapProps}/>
          <Button
            variant="contained"
            color="primary"
            sx={{
              position: "absolute",
              padding: "6px 10px",
              bottom: "10px",
              right: "150px",
              zIndex: 10,
              fontSize: "10px"
            }}
            onClick={() => fitToMarkers()}
          >
            Reset View
          </Button>
        </Box>
    </Box>
  );
};

export default MapView;
